import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  createAssessment,
  listAssessments,
  listSubmissions,
  listMyJobs,
} from "@/lib/companyApi";
import { Plus, Trash2, ArrowLeft, Loader2, ClipboardList } from "lucide-react";

const TYPES = [
  { k: "mcq", l: "Multiple choice" },
  { k: "short", l: "Short answer" },
  { k: "coding", l: "Coding" },
];

export function AssessmentsList() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await listAssessments();
      setList(data);
      setLoading(false);
    })();
  }, []);

  return (
    <div className="container-x py-10 md:py-14">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <span className="overline">Assessments</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
            AI-graded role-tests.{" "}
            <span className="italic text-stone-500">Five minutes to build.</span>
          </h1>
        </div>
        <Link to="/company/assessments/new" data-testid="assessments-new" className="btn-primary">
          <Plus size={16} /> New assessment
        </Link>
      </div>

      {loading ? (
        <p className="text-stone-500">Loading…</p>
      ) : list.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-stone-300 bg-[#F5F3ED] p-10 text-center">
          <ClipboardList className="mx-auto mb-3" size={20} />
          <p className="text-stone-700 font-display text-2xl">No assessments yet.</p>
          <Link to="/company/assessments/new" className="btn-primary mt-5 inline-flex">
            <Plus size={16} /> Create your first assessment
          </Link>
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((a) => (
            <li key={a.assessment_id} className="rounded-3xl border border-stone-200 bg-white p-6" data-testid={`as-card-${a.assessment_id}`}>
              <p className="overline">{a.questions?.length || 0} questions · {a.duration_min}min</p>
              <h3 className="font-display text-xl mt-2">{a.title}</h3>
              <Link
                to={`/company/assessments/${a.assessment_id}`}
                data-testid={`as-open-${a.assessment_id}`}
                className="mt-5 inline-flex items-center gap-1 text-sm text-[#C2410C] hover:text-stone-900"
              >
                Open submissions →
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AssessmentNew() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [duration, setDuration] = useState(30);
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [questions, setQuestions] = useState([blankQ("mcq")]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await listMyJobs();
        setJobs(data);
      } catch (e) {}
    })();
  }, []);

  function blankQ(type) {
    if (type === "mcq")
      return { type, prompt: "", options: ["", "", "", ""], correct: "", weight: 1 };
    if (type === "coding")
      return { type, prompt: "", language: "javascript", weight: 2 };
    return { type, prompt: "", weight: 1 };
  }

  const updateQ = (i, patch) =>
    setQuestions((qs) => qs.map((q, k) => (k === i ? { ...q, ...patch } : q)));

  const updateOpt = (i, j, val) =>
    setQuestions((qs) =>
      qs.map((q, k) =>
        k === i
          ? { ...q, options: q.options.map((o, oj) => (oj === j ? val : o)) }
          : q
      )
    );

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    for (const q of questions) {
      if (!q.prompt.trim()) {
        setError("Every question needs a prompt");
        return;
      }
      if (q.type === "mcq" && !q.correct) {
        setError("Every MCQ needs a correct option marked");
        return;
      }
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        title,
        duration_min: Number(duration) || 30,
        job_id: jobId || null,
        questions: questions.map((q) => ({
          ...q,
          options: q.type === "mcq" ? q.options.filter(Boolean) : undefined,
        })),
      };
      const { data } = await createAssessment(payload);
      navigate(`/company/assessments/${data.assessment_id}`, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not create assessment");
      setSubmitting(false);
    }
  };

  return (
    <div className="container-x py-10 md:py-14 max-w-4xl">
      <Link to="/company/assessments" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900 mb-6">
        <ArrowLeft size={14} /> Back
      </Link>
      <h1 className="font-display text-4xl md:text-5xl mb-8 leading-tight">
        Build an assessment <span className="italic text-stone-500">in 5 minutes.</span>
      </h1>

      <div className="space-y-4">
        <div>
          <label className="overline">Title *</label>
          <input
            type="text"
            data-testid="as-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Senior Backend Engineer · Take-home #1"
            className="mt-2 w-full px-4 py-3 rounded-full bg-white border border-stone-300 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="overline">Time limit (minutes)</label>
            <input
              type="number"
              data-testid="as-duration"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              min={5}
              max={240}
              className="mt-2 w-full px-4 py-3 rounded-full bg-white border border-stone-300 focus:outline-none focus:border-stone-900 text-sm"
            />
          </div>
          <div>
            <label className="overline">Attach to job (optional)</label>
            <select
              data-testid="as-job"
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              className="mt-2 w-full px-4 py-3 rounded-full bg-white border border-stone-300 focus:outline-none focus:border-stone-900 text-sm"
            >
              <option value="">— No job —</option>
              {jobs.map((j) => (
                <option key={j.job_id} value={j.job_id}>
                  {j.title}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="mt-10 space-y-5">
        {questions.map((q, i) => (
          <div key={i} className="rounded-3xl bg-white border border-stone-200 p-6" data-testid={`q-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono text-xs text-stone-500 uppercase tracking-widest">
                Question {i + 1}
              </span>
              <div className="flex items-center gap-2">
                <select
                  data-testid={`q-${i}-type`}
                  value={q.type}
                  onChange={(e) => {
                    const t = e.target.value;
                    setQuestions((qs) => qs.map((qq, k) => (k === i ? blankQ(t) : qq)));
                  }}
                  className="px-3 py-1.5 rounded-full bg-stone-100 border border-stone-300 text-sm"
                >
                  {TYPES.map((t) => (
                    <option key={t.k} value={t.k}>
                      {t.l}
                    </option>
                  ))}
                </select>
                {questions.length > 1 && (
                  <button
                    type="button"
                    data-testid={`q-${i}-remove`}
                    onClick={() => setQuestions((qs) => qs.filter((_, k) => k !== i))}
                    className="p-1.5 rounded-full text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <textarea
              data-testid={`q-${i}-prompt`}
              value={q.prompt}
              onChange={(e) => updateQ(i, { prompt: e.target.value })}
              rows={2}
              placeholder="Question prompt…"
              className="w-full px-4 py-3 rounded-2xl bg-stone-50 border border-stone-300 focus:outline-none focus:border-stone-900 text-sm"
            />
            {q.type === "mcq" && (
              <div className="mt-3 space-y-2">
                {q.options.map((o, j) => (
                  <div key={j} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={`correct-${i}`}
                      checked={q.correct === o && !!o}
                      onChange={() => updateQ(i, { correct: o })}
                      disabled={!o}
                      data-testid={`q-${i}-correct-${j}`}
                    />
                    <input
                      type="text"
                      data-testid={`q-${i}-opt-${j}`}
                      value={o}
                      onChange={(e) => updateOpt(i, j, e.target.value)}
                      placeholder={`Option ${j + 1}`}
                      className="flex-1 px-3 py-2 rounded-full bg-white border border-stone-300 focus:outline-none focus:border-stone-900 text-sm"
                    />
                  </div>
                ))}
                <p className="overline">Mark the radio next to the correct option.</p>
              </div>
            )}
            {q.type === "coding" && (
              <input
                type="text"
                value={q.language || ""}
                onChange={(e) => updateQ(i, { language: e.target.value })}
                placeholder="Language (e.g. python, javascript, go)"
                className="mt-3 w-full px-4 py-2.5 rounded-full bg-white border border-stone-300 focus:outline-none focus:border-stone-900 text-sm"
                data-testid={`q-${i}-lang`}
              />
            )}
            <div className="mt-3 flex items-center gap-3">
              <span className="overline">Weight</span>
              <input
                type="number"
                min={1}
                max={5}
                value={q.weight}
                onChange={(e) => updateQ(i, { weight: Number(e.target.value) || 1 })}
                className="w-20 px-3 py-1.5 rounded-full bg-white border border-stone-300 text-sm focus:outline-none focus:border-stone-900"
                data-testid={`q-${i}-weight`}
              />
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={() => setQuestions((qs) => [...qs, blankQ("short")])}
          data-testid="q-add"
          className="inline-flex items-center gap-1 text-sm font-medium text-[#C2410C] hover:text-stone-900"
        >
          <Plus size={14} /> Add another question
        </button>
      </div>

      {error && <p className="mt-4 text-red-700 text-sm" data-testid="as-error">{error}</p>}

      <div className="mt-8 flex items-center gap-3">
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          data-testid="as-submit"
          className="btn-primary disabled:opacity-60"
        >
          {submitting ? <><Loader2 size={16} className="animate-spin" /> Saving</> : "Publish assessment"}
        </button>
      </div>
    </div>
  );
}

export function AssessmentSubmissions() {
  const { id } = useParams();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await listSubmissions(id);
        setSubs(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  return (
    <div className="container-x py-10 md:py-14">
      <Link to="/company/assessments" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900 mb-6">
        <ArrowLeft size={14} /> Back to assessments
      </Link>
      <h1 className="font-display text-4xl md:text-5xl leading-tight mb-3">
        Submissions <span className="italic text-stone-500">· ranked by AI grade.</span>
      </h1>
      <p className="overline mb-8">Share the link below with candidates:</p>
      <div className="rounded-2xl bg-stone-100 border border-stone-200 p-4 mb-10 font-mono text-sm break-all">
        {window.location.origin}/app/assessment/{id}
      </div>

      {loading ? (
        <p className="text-stone-500">Loading…</p>
      ) : subs.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-stone-300 bg-[#F5F3ED] p-10 text-center text-stone-600">
          No submissions yet.
        </div>
      ) : (
        <ul className="space-y-3">
          {subs.map((s) => (
            <li key={s.submission_id} className="rounded-2xl bg-white border border-stone-200 p-5" data-testid={`sub-${s.submission_id}`}>
              <button
                onClick={() => setExpanded((e) => (e === s.submission_id ? null : s.submission_id))}
                className="w-full flex items-center justify-between"
              >
                <div className="text-left">
                  <p className="font-medium">{s.candidate_name}</p>
                  <p className="text-stone-500 text-sm">{s.candidate_email}</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-display text-3xl" style={{ color: scoreColor(s.total_score) }}>
                    {s.total_score}
                  </span>
                  <span className="overline">{s.grade?.verdict}</span>
                </div>
              </button>
              {expanded === s.submission_id && (
                <div className="mt-5 border-t border-stone-200 pt-5 space-y-3">
                  <p className="text-stone-700">{s.grade?.summary}</p>
                  {(s.grade?.per_question || []).map((pq, i) => (
                    <div key={i} className="rounded-xl bg-stone-50 border border-stone-200 p-3">
                      <p className="text-sm font-medium">
                        Q{pq.index + 1} · <span style={{ color: scoreColor(pq.score) }}>{pq.score}</span>
                      </p>
                      <p className="text-stone-600 text-sm mt-1">{pq.feedback}</p>
                    </div>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function scoreColor(s) {
  if (s >= 80) return "#15803D";
  if (s >= 60) return "#C2410C";
  return "#78716C";
}
